import { clientFetch } from '@/utils/service';
import { isFunction, qsToObj } from '@/utils/helpers';
import { toast } from 'react-toastify';
import * as types from './types';

const api = (path, options) => clientFetch(`/admin/${path}`, options);
const query = (qs) => (qs ? qsToObj(qs) : {});
const action = (type, payload) => ({ type, payload });
const manage = async ({ type, path, id, data, dispatch, onSuccess, onError, fileUpload = false }) => {
  try {
    const result = await api(id ? `${path}/${id}` : path, {
      method: id ? 'PATCH' : 'POST',
      body: fileUpload ? data : JSON.stringify(data),
      fileUpload,
    });
    dispatch(action(`${type}_FULFILLED`, result));
    isFunction(onSuccess) && onSuccess(result);
    return result;
  } catch (error) {
    dispatch(action(`${type}_REJECTED`, error));
    isFunction(onError) && onError(error);
    return undefined;
  }
};

export const fetchNutritionists = (qs) => action(types.FETCH_NUTRITIONISTS, api('nutritionists', { query: query(qs) }));
export const fetchNutritionistApi = (id) => api(`nutritionists/${id}`);
export const fetchNutritionistSchedulersApi = (id) => clientFetch(`/nutritionists/${id}/schedulers`);
export const fetchNutritionist = (id) => action(types.FETCH_NUTRITIONIST, fetchNutritionistApi(id));
export const manageNutritionist = (args) => manage({
  ...args,
  type: args.id ? types.UPDATE_NUTRITIONIST : types.CREATE_NUTRITIONIST,
  path: 'nutritionists',
});
export const addNutritionDiploma = ({ data, dispatch, onSuccess, onError }) => manage({ type: types.CREATE_NUTRITION_DIPLOMA, path: 'nutritionists/diploma', data, dispatch, onSuccess, onError });
export const deleteNutritionDiploma = (id, { t, onSuccess }) => action(types.DELETE_NUTRITION_DIPLOMA, api(`nutritionists/diploma/${id}`, { method: 'DELETE' }).then(() => {
  toast.success(t('nutrition.diploma_deleted'));
  isFunction(onSuccess) && onSuccess();
  return null;
}));

export const fetchNutritionConsultations = (qs) => action(types.FETCH_NUTRITION_CONSULTATIONS, api('nutrition-consultations', { query: query(qs) }));
export const fetchNutritionConsultation = (id) => action(types.FETCH_NUTRITION_CONSULTATION, api(`nutrition-consultations/${id}`));
export const submitNutritionConsultation = (args) => manage({ ...args, type: args.id ? types.UPDATE_NUTRITION_CONSULTATION : types.CREATE_NUTRITION_CONSULTATION, path: 'nutrition-consultations' });
export const cancelNutritionConsultation = (id, { t, onSuccess, onError }) => action(types.CANCEL_NUTRITION_CONSULTATION, api(`nutrition-consultations/${id}/cancel`).then((result) => {
  toast.success(t('nutrition.consultation_canceled'));
  isFunction(onSuccess) && onSuccess(result);
  return { id };
}).catch((error) => {
  toast.error(t('nutrition.save_error'));
  isFunction(onError) && onError(error);
  throw error;
}));
export const completeNutritionConsultation = (id, { t, onSuccess }) => action(types.COMPLETE_NUTRITION_CONSULTATION, api(`nutrition-consultations/${id}/complete`).then((result) => {
  toast.success(t('nutrition.consultation_completed'));
  isFunction(onSuccess) && onSuccess(result);
  return { id };
}));

export const fetchNutritionTasks = (consultationId) => action(types.FETCH_NUTRITION_TASKS, api(`nutrition-tasks/${consultationId}/consultation`));
export const manageNutritionTask = (args) => manage({ ...args, type: args.id ? types.UPDATE_NUTRITION_TASK : types.CREATE_NUTRITION_TASK, path: 'nutrition-tasks' });
export const deleteNutritionTask = (id) => action(types.DELETE_NUTRITION_TASK, api(`nutrition-tasks/${id}`, { method: 'DELETE' }).then(() => ({ id })));
export const uploadNutritionTaskFile = ({ file, taskId, userId, dispatch, onSuccess, onError }) => {
  const formData = new FormData();
  formData.append('file', file); formData.append('task', taskId); formData.append('user', userId);
  return manage({ type: types.UPLOAD_NUTRITION_TASK_FILE, path: 'nutrition-tasks-file', data: formData, dispatch, onSuccess, onError, fileUpload: true });
};
export const deleteNutritionTaskFile = ({ fileId, taskId }) => action(types.DELETE_NUTRITION_TASK_FILE, api(`nutrition-tasks-file/${fileId}`, { method: 'DELETE' }).then(() => ({ fileId, taskId })));

export const fetchNutritionNotes = (consultationId) => action(types.FETCH_NUTRITION_NOTES, api(`nutrition-consultations/note/${consultationId}/consultation`));
export const manageNutritionNote = (args) => manage({ ...args, type: args.id ? types.UPDATE_NUTRITION_NOTE : types.CREATE_NUTRITION_NOTE, path: 'nutrition-consultations/note' });
export const deleteNutritionNote = (id) => action(types.DELETE_NUTRITION_NOTE, api(`nutrition-consultations/note/${id}`, { method: 'DELETE' }).then(() => ({ id })));

export const fetchNutritionFeedbacks = (qs) => action(types.FETCH_NUTRITION_FEEDBACKS, api('nutrition-feedback', { query: query(qs) }));
export const manageNutritionFeedback = (args) => manage({ ...args, type: types.UPDATE_NUTRITION_FEEDBACK, path: 'nutrition-feedback' });
export const deleteNutritionFeedback = (id) => action(types.DELETE_NUTRITION_FEEDBACK, api(`nutrition-feedback/${id}`, { method: 'DELETE' }).then(() => ({ id })));

export const fetchNutritionFilters = (qs) => action(types.FETCH_NUTRITION_FILTERS, api('nutrition-filters', { query: query(qs) }));
export const manageNutritionFilter = (args) => manage({ ...args, type: args.id ? types.UPDATE_NUTRITION_FILTER : types.CREATE_NUTRITION_FILTER, path: 'nutrition-filters' });
export const deleteNutritionFilter = (id) => action(types.DELETE_NUTRITION_FILTER, api(`nutrition-filters/${id}`, { method: 'DELETE' }).then(() => ({ id })));

export const fetchNutritionFeatures = () => action(types.FETCH_NUTRITION_FEATURES, api('nutrition-features'));
export const manageNutritionFeature = (args) => manage({ ...args, type: args.id ? types.UPDATE_NUTRITION_FEATURE : types.CREATE_NUTRITION_FEATURE, path: 'nutrition-features' });
export const deleteNutritionFeature = (id) => action(types.DELETE_NUTRITION_FEATURE, api(`nutrition-features/${id}`, { method: 'DELETE' }).then(() => ({ id })));
export const fetchNutritionAgora = (id) => action(types.FETCH_NUTRITION_AGORA, api(`nutrition-consultations/${id}`));
